import { useCallback } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ChatSendResponse } from '@wundo/shared/chat.ts';
import type { OnboardingNudgeMessageSource } from '@wundo/shared/onboardingNudge.ts';
import { sendChatMessage } from './chat.ts';
import type { AnsweringOpening, ConversationalOpening } from './chatPageOpening.ts';
import {
  sendMeetingDebrief,
  sendMeetingIntake,
  sendMeetingRecap,
} from './meetingPage.ts';

/* The reply to an opening is sent to the endpoint for that opening's kind. */
const SEND_OPENING_ANSWER = {
  intake: sendMeetingIntake,
  debrief: sendMeetingDebrief,
  recap: sendMeetingRecap,
} satisfies Record<ConversationalOpening['kind'], unknown>;

export interface ChatSendOptions {
  conversationId: string | null;
  startingNew: boolean;
  /** The page's opening, while the next message is the first answer to it. */
  answering: AnsweringOpening | null;
  /** The files that finished uploading, to go with the message. */
  attachmentIds: string[];
  /** A message is on its way, however it was sent: typed, dictated or a button. */
  onSending: () => void;
  onSent: (response: ChatSendResponse) => void;
  onFailed: () => void;
}

export interface ChatSend {
  /** Send these words as they are, saying which button sent them, if any. */
  send: (text: string, source?: OnboardingNudgeMessageSource) => void;
  pending: boolean;
}

/**
 * Sending a message to wherever the drawer stands: the first answer to the
 * page's opening goes to that opening's own door, a new conversation is asked
 * for as one, and everything else continues the conversation on screen.
 */
export function useChatSend({
  conversationId,
  startingNew,
  answering,
  attachmentIds,
  onSending,
  onSent,
  onFailed,
}: ChatSendOptions): ChatSend {
  const mutation = useMutation({
    mutationFn: async ({
      text,
      source,
    }: {
      text: string;
      source?: OnboardingNudgeMessageSource;
    }) => {
      if (answering) {
        const sendAnswer = SEND_OPENING_ANSWER[answering.kind];
        return await sendAnswer(
          answering.userId,
          answering.meetingId,
          text,
          attachmentIds,
        );
      }
      if (startingNew) {
        return await sendChatMessage(text, { startNew: true, attachmentIds, source });
      }
      return await sendChatMessage(text, {
        conversationId: conversationId ?? undefined,
        attachmentIds,
        source,
      });
    },
    onMutate: () => onSending(),
    onSuccess: onSent,
    onError: onFailed,
  });

  const { mutate } = mutation;
  const send = useCallback(
    (text: string, source?: OnboardingNudgeMessageSource) => mutate({ text, source }),
    [mutate],
  );

  return { send, pending: mutation.isPending };
}
